'use client'

import { useEffect, useState } from 'react'
import {
  FEATURE_CONTROL_DEFAULTS,
  LEGACY_FLAG_ALIASES,
  flattenFeatureBooleans,
  isFeatureVisible,
  normalizeFeatureControls,
  type FeatureControl,
  type FeatureControlKey,
  type FeatureControls,
  type FeatureSettingKey,
} from '../lib/feature-control'

export type { FeatureControl, FeatureControlKey, FeatureControls, FeatureSettingKey }
export type FlagKey = FeatureControlKey | keyof typeof LEGACY_FLAG_ALIASES
export type FeatureFlag = FeatureControl

export const FLAG_DEFAULTS = FEATURE_CONTROL_DEFAULTS
const LS_KEY = 'masjids_feature_controls_v1'

export function getControls(): FeatureControls {
  if (typeof window === 'undefined') return FEATURE_CONTROL_DEFAULTS as FeatureControls
  try {
    const raw = window.localStorage.getItem(LS_KEY)
    return normalizeFeatureControls(raw ? JSON.parse(raw) : undefined)
  } catch {
    return FEATURE_CONTROL_DEFAULTS as FeatureControls
  }
}

export function getFlags(): FeatureControls {
  return getControls()
}

/* Result of a flag write. `ok` mirrors the old boolean; `error`/`status` carry the REASON so a
   failed write is surfaced to the admin instead of silently reverting ("clicking does nothing"). */
export interface SaveControlsResult {
  ok: boolean
  error?: string
  status?: number
}

/* Persists via POST /api/admin/flags. The local cache write is best-effort and never masks a
   failed persist. On failure the actual status + server message are returned so the caller can
   show them + revert. */
export async function saveControls(controls: FeatureControls): Promise<SaveControlsResult> {
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(controls)) } catch {}
  try {
    const res = await fetch('/api/admin/flags', {
      method: 'POST',
      // Send the admin auth cookie with the write — without this the POST can 401/403.
      // httpOnly cookies are still sent by the browser; JS just can't read them.
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ controls }),
    })
    if (res.ok) return { ok: true }
    // Surface the real reason — server message if present, else a clear status-based one.
    let error = `Save failed (HTTP ${res.status}).`
    try { const b = await res.json(); if (b && typeof b.error === 'string' && b.error) error = b.error } catch {}
    if (res.status === 401 || res.status === 403) {
      error = `Not authorized to change flags (HTTP ${res.status}). Your admin session may have expired — sign in again.`
    }
    return { ok: false, error, status: res.status }
  } catch (e) {
    return { ok: false, error: e instanceof Error ? `Network error: ${e.message}` : 'Network error — the flag was not saved.' }
  }
}

export async function saveFlags(flags: FeatureControls) {
  return saveControls(flags)
}

export function useFeatureFlags() {
  const [controls, setControls] = useState<FeatureControls>(FEATURE_CONTROL_DEFAULTS as FeatureControls)

  useEffect(() => {
    setControls(getControls())
    fetch('/api/admin/flags?full=true')
      .then((r) => r.json())
      .then((payload) => {
        const next = normalizeFeatureControls(payload.controls || payload)
        setControls(next)
        try { window.localStorage.setItem(LS_KEY, JSON.stringify(next)) } catch {}
      })
      .catch(() => {})

    const handler = () => setControls(getControls())
    window.addEventListener('flagsUpdated', handler)
    return () => window.removeEventListener('flagsUpdated', handler)
  }, [])

  const isFeatureSettingEnabled = (key: FeatureControlKey, setting: FeatureSettingKey = 'publicVisible') => {
    return isFeatureVisible(controls, key, setting)
  }

  const isFlagEnabled = (key: FlagKey) => {
    const controlKey = ((LEGACY_FLAG_ALIASES as Record<string, FeatureControlKey>)[String(key)] || key) as FeatureControlKey
    return isFeatureSettingEnabled(controlKey, 'publicVisible')
  }

  const booleanFlags = flattenFeatureBooleans(controls)

  return {
    flags: controls,
    controls,
    booleanFlags,
    isFlagEnabled,
    isFeatureSettingEnabled,
  }
}
